/**
 * Service-worker update plumbing. The SW already calls `skipWaiting()` +
 * `clientsClaim()` so a new SW takes over immediately on activation. This
 * module wires the page-side bits:
 *
 *   1. Reload the page when a new SW takes control (`controllerchange`) so the
 *      user instantly gets the fresh bundle without manually pulling-to-refresh.
 *   2. Poll `registration.update()` every minute while the tab is open, plus
 *      whenever the tab regains visibility, so a deploy is picked up within
 *      minutes of returning to the app.
 *   3. Expose `forceAppUpdate()` so a manual "check for updates" button can
 *      blow away caches and reload.
 */

import { guardedReload } from "./reload-guard";
import { requestReload } from "./reload-defer";

let reloading = false;

export function installServiceWorkerUpdater(): void {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloading) return;
    reloading = true;
    // Reload so the page runs under the new SW — but via the circuit-breaker so
    // a SW that keeps re-taking control can't drive an endless refresh loop.
    // An open Card Table holds reloads (reload-defer): the reload waits for the
    // "Update ready — reload" toast or for the player to leave the table.
    requestReload(() => guardedReload());
  });

  navigator.serviceWorker.ready
    .then((registration) => {
      // Kick off an update check on boot and on a steady interval.
      void registration.update().catch(() => {});
      window.setInterval(() => {
        void registration.update().catch(() => {});
      }, 60_000);

      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          void registration.update().catch(() => {});
        }
      });
    })
    .catch(() => {});
}

export async function forceAppUpdate(): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name)));
    }
  } finally {
    // Cache-bust the navigation so we fetch the live index.html, not a stale
    // HTTP-cached shell pointing at purged chunk hashes.
    const u = new URL(window.location.href);
    u.searchParams.set("_fresh", Date.now().toString(36));
    window.location.replace(u.pathname + u.search + u.hash);
  }
}
